import { BaseScene } from './BaseScene';
import { LeaderboardManager, LeaderboardEntry, LeaderboardConfig } from '../systems/LeaderboardManager';

export class LeaderboardScene extends BaseScene {
  private leaderboardManager: LeaderboardManager;
  private currentLeaderboard: string = 'highest_level';
  private contentContainer: Phaser.GameObjects.Container;
  private playerName: string;

  constructor() {
    super({ key: 'Leaderboard' });
  }

  override create(): void {
    super.create();
    this.addBackground();
    
    this.leaderboardManager = LeaderboardManager.getInstance();
    this.playerName = this.getPlayerName();
    
    this.createHeader();
    this.createLeaderboardTabs();
    this.createSeasonInfo();
    this.createContentContainer();
    this.updateContent();
  }

  private getPlayerName(): string {
    // Get player name from profile or use default
    const userProfile = (window as any).userProfile;
    return userProfile?.playerName || 'Player';
  }

  private createHeader(): void {
    const centerX = this.cameras.main.centerX;
    
    // Title
    this.add.text(centerX, 50, 'LEADERBOARDS', {
      fontSize: '36px',
      color: '#FFD700',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Back button
    const backBtn = this.add.text(50, 40, '← BACK', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#666666',
      padding: { x: 15, y: 8 },
      fontFamily: 'Arial',
    })
    .setOrigin(0, 0.5)
    .setInteractive({ useHandCursor: true });
    
    backBtn.on('pointerdown', () => {
      this.audioManager.playButtonClick();
      this.scene.start('MainMenu');
    });
    
    backBtn.on('pointerover', () => {
      this.audioManager.playButtonHover();
      backBtn.setStyle({ backgroundColor: '#888888' });
    });
    
    backBtn.on('pointerout', () => {
      backBtn.setStyle({ backgroundColor: '#666666' });
    });
  }

  private createLeaderboardTabs(): void {
    const configs = this.leaderboardManager.getLeaderboardConfigs();
    const tabWidth = 120;
    const totalWidth = configs.length * tabWidth;
    const startX = this.cameras.main.centerX - totalWidth / 2;
    const tabY = 100;
    
    configs.forEach((config, index) => {
      const isActive = this.currentLeaderboard === config.id;
      const x = startX + index * tabWidth + tabWidth / 2;
      
      const tab = this.add.text(x, tabY, config.name, {
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: isActive ? '#4CAF50' : '#444444',
        padding: { x: 6, y: 8 },
        fontFamily: 'Arial',
        align: 'center',
        wordWrap: { width: tabWidth - 10 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
      
      tab.on('pointerdown', () => {
        this.audioManager.playButtonClick();
        this.currentLeaderboard = config.id;
        this.updateContent();
        this.updateTabs();
      });
      
      if (!isActive) {
        tab.on('pointerover', () => {
          this.audioManager.playButtonHover();
          tab.setStyle({ backgroundColor: '#666666' });
        });
        
        tab.on('pointerout', () => {
          tab.setStyle({ backgroundColor: '#444444' });
        });
      }
      
      // Store reference for updating
      tab.setName(`tab_${config.id}`);
    });
  }

  private updateTabs(): void {
    const configs = this.leaderboardManager.getLeaderboardConfigs();
    
    configs.forEach(config => {
      const tab = this.children.getByName(`tab_${config.id}`) as Phaser.GameObjects.Text;
      if (tab) {
        const isActive = this.currentLeaderboard === config.id;
        tab.setStyle({ backgroundColor: isActive ? '#4CAF50' : '#444444' });
        
        if (isActive) {
          tab.removeInteractive();
        } else {
          tab.setInteractive({ useHandCursor: true });
        }
      }
    });
  }

  private createSeasonInfo(): void {
    const season = this.leaderboardManager.getCurrentSeason();
    if (!season) return;
    
    const centerX = this.cameras.main.centerX;
    const seasonY = 140;
    
    // Season info panel
    this.add.rectangle(centerX, seasonY, 400, 40, 0x1a1a1a, 0.8)
      .setStrokeStyle(2, 0xFFD700);
    
    // Season name
    this.add.text(centerX - 150, seasonY, `🏆 ${season.name}`, {
      fontSize: '14px',
      color: '#FFD700',
      fontFamily: 'Arial',
    }).setOrigin(0, 0.5);
    
    // Time remaining
    const timeRemaining = this.leaderboardManager.getSeasonTimeRemaining();
    const timeText = this.leaderboardManager.formatTime(timeRemaining);
    this.add.text(centerX + 150, seasonY, `⏱️ ${timeText} left`, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(1, 0.5);
  }

  private createContentContainer(): void {
    this.contentContainer = this.add.container(0, 180);
  }

  private updateContent(): void {
    this.contentContainer.removeAll(true);
    
    const config = this.leaderboardManager.getLeaderboardConfigs()
      .find(c => c.id === this.currentLeaderboard);
    
    if (!config) return;
    
    // Leaderboard header
    this.createLeaderboardHeader(config);
    
    // Leaderboard entries
    const entries = this.leaderboardManager.getLeaderboard(this.currentLeaderboard, 20);
    this.createLeaderboardEntries(entries, config);
    
    // Player stats
    this.createPlayerStats(config);
    
    // Leaderboard stats
    this.createLeaderboardStats(config);
  }

  private createLeaderboardHeader(config: LeaderboardConfig): void {
    const centerX = this.cameras.main.centerX;
    
    // Header background
    const header = this.add.rectangle(centerX, 20, 700, 40, 0x2d2d2d, 0.9);
    this.contentContainer.add(header);
    
    // Icon and title
    const title = this.add.text(centerX, 20, `${config.icon} ${config.name}`, {
      fontSize: '18px',
      color: '#FFD700',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.contentContainer.add(title);
    
    // Description
    const description = this.add.text(centerX, 45, config.description, {
      fontSize: '12px',
      color: '#cccccc',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    this.contentContainer.add(description);
  }

  private createLeaderboardEntries(entries: LeaderboardEntry[], config: LeaderboardConfig): void {
    const centerX = this.cameras.main.centerX;
    let yOffset = 80;
    
    if (entries.length === 0) {
      const noData = this.add.text(centerX, yOffset + 50, 'No entries yet. Be the first!', {
        fontSize: '16px',
        color: '#888888',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      this.contentContainer.add(noData);
      return;
    }
    
    // Column headers
    const headerBg = this.add.rectangle(centerX, yOffset, 680, 30, 0x333333, 0.8);
    this.contentContainer.add(headerBg);
    
    const headers = ['RANK', 'PLAYER', 'SCORE', 'LEVEL', 'DATE'];
    const headerPositions = [-280, -180, -50, 50, 180];
    
    headers.forEach((header, index) => {
      const headerText = this.add.text(centerX + (headerPositions[index] ?? 0), yOffset, header, {
        fontSize: '12px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      this.contentContainer.add(headerText);
    });
    
    yOffset += 40;
    
    // Entries
    entries.forEach((entry, index) => {
      this.createLeaderboardEntry(entry, index + 1, yOffset, config);
      yOffset += 35;
    });
  }

  private createLeaderboardEntry(entry: LeaderboardEntry, rank: number, y: number, config: LeaderboardConfig): void {
    const centerX = this.cameras.main.centerX;
    const isPlayerEntry = entry.playerName === this.playerName;
    
    // Background
    const bgColor = isPlayerEntry ? 0x2d4a2d : (rank <= 3 ? 0x4a3d2d : 0x2d2d2d);
    const entryBg = this.add.rectangle(centerX, y, 680, 30, bgColor, 0.7);
    this.contentContainer.add(entryBg);
    
    // Rank with medal for top 3
    let rankText = rank.toString();
    let rankColor = '#ffffff';
    
    if (rank === 1) {
      rankText = '🥇 1';
      rankColor = '#FFD700';
    } else if (rank === 2) {
      rankText = '🥈 2';
      rankColor = '#C0C0C0';
    } else if (rank === 3) {
      rankText = '🥉 3';
      rankColor = '#CD7F32';
    }
    
    const rankDisplay = this.add.text(centerX - 280, y, rankText, {
      fontSize: '14px',
      color: rankColor,
      fontFamily: 'Arial',
      fontStyle: rank <= 3 ? 'bold' : 'normal',
    }).setOrigin(0.5);
    this.contentContainer.add(rankDisplay);
    
    // Player name (highlight if current player)
    const playerName = this.add.text(centerX - 180, y, entry.playerName, {
      fontSize: '14px',
      color: isPlayerEntry ? '#4CAF50' : '#ffffff',
      fontFamily: 'Arial',
      fontStyle: isPlayerEntry ? 'bold' : 'normal',
    }).setOrigin(0.5);
    this.contentContainer.add(playerName);
    
    // Score (formatted based on leaderboard type)
    const scoreText = this.formatScore(entry.score, config);
    const score = this.add.text(centerX - 50, y, scoreText, {
      fontSize: '14px',
      color: '#FFD700',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    this.contentContainer.add(score);
    
    // Level
    const level = this.add.text(centerX + 50, y, entry.level.toString(), {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    this.contentContainer.add(level);
    
    // Date
    const date = new Date(entry.completedAt).toLocaleDateString();
    const dateText = this.add.text(centerX + 180, y, date, {
      fontSize: '12px',
      color: '#cccccc',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    this.contentContainer.add(dateText);
    
    // Earth type indicator
    const earthIcon = this.getEarthIcon(entry.earthType);
    const earth = this.add.text(centerX + 250, y, earthIcon, {
      fontSize: '16px',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    this.contentContainer.add(earth);
  }

  private formatScore(score: number, config: LeaderboardConfig): string {
    switch (config.scoreType) {
      case 'time':
        if (config.id === 'speed_run') {
          // Format as minutes:seconds for speed runs
          const minutes = Math.floor(score / 60000);
          const seconds = Math.floor((score % 60000) / 1000);
          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        } else {
          // Format as hours:minutes for survival time
          const hours = Math.floor(score / 3600000);
          const minutes = Math.floor((score % 3600000) / 60000);
          return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        }
      case 'level':
      case 'monsters':
      case 'composite':
      default:
        return score.toLocaleString();
    }
  }

  private getEarthIcon(earthType: string): string {
    const earthIcons: { [key: string]: string } = {
      'classic': '🌍',
      'ice': '🧊',
      'volcanic': '🌋',
      'crystal': '💎',
      'quantum': '⚛️',
      'neutron': '⭐',
    };
    
    return earthIcons[earthType] || '🌍';
  }

  private createPlayerStats(config: LeaderboardConfig): void {
    const centerX = this.cameras.main.centerX;
    const yOffset = 300;
    
    // Player stats section
    const statsHeader = this.add.text(centerX, yOffset, 'YOUR BEST', {
      fontSize: '16px',
      color: '#4CAF50',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.contentContainer.add(statsHeader);
    
    const playerRank = this.leaderboardManager.getPlayerRank(this.currentLeaderboard, this.playerName);
    
    if (playerRank) {
      const statsText = `Rank #${playerRank.rank} - Score: ${this.formatScore(playerRank.entry.score, config)}`;
      const playerStats = this.add.text(centerX, yOffset + 25, statsText, {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      this.contentContainer.add(playerStats);
    } else {
      const noStats = this.add.text(centerX, yOffset + 25, 'No entries yet', {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      this.contentContainer.add(noStats);
    }
  }

  private createLeaderboardStats(config: LeaderboardConfig): void {
    const centerX = this.cameras.main.centerX;
    const yOffset = 370;
    
    const stats = this.leaderboardManager.getLeaderboardStats(this.currentLeaderboard);
    
    if (stats.totalEntries === 0) return;
    
    // Stats background
    const statsBg = this.add.rectangle(centerX, yOffset + 30, 500, 60, 0x1a1a1a, 0.8)
      .setStrokeStyle(1, 0x444444);
    this.contentContainer.add(statsBg);
    
    // Stats title
    const statsTitle = this.add.text(centerX, yOffset, 'LEADERBOARD STATS', {
      fontSize: '14px',
      color: '#FFD700',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.contentContainer.add(statsTitle);
    
    // Stats data
    const statsData = [
      `Total Entries: ${stats.totalEntries}`,
      `Top Score: ${this.formatScore(stats.topScore, config)}`,
      `Average: ${this.formatScore(stats.averageScore, config)}`,
    ];
    
    statsData.forEach((stat, index) => {
      const statText = this.add.text(centerX, yOffset + 20 + (index * 15), stat, {
        fontSize: '12px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      this.contentContainer.add(statText);
    });
  }

  // Show new record notification
  public showNewRecord(leaderboardId: string, rank: number): void {
    const config = this.leaderboardManager.getLeaderboardConfigs()
      .find(c => c.id === leaderboardId);
    
    if (!config) return;
    
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    
    // Background
    const overlay = this.add.rectangle(0, 0, this.cameras.main.width, this.cameras.main.height, 0x000000, 0.7)
      .setOrigin(0)
      .setInteractive();
    
    // Record panel
    const panel = this.add.rectangle(centerX, centerY, 400, 200, 0x1a1a1a, 0.95)
      .setStrokeStyle(3, 0xFFD700);
    
    // Title
    this.add.text(centerX, centerY - 60, '🎉 NEW RECORD!', {
      fontSize: '24px',
      color: '#FFD700',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    
    // Leaderboard info
    this.add.text(centerX, centerY - 20, `${config.icon} ${config.name}`, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Rank
    let rankText = `Rank #${rank}`;
    if (rank === 1) rankText = '🥇 #1 - NEW WORLD RECORD!';
    else if (rank === 2) rankText = '🥈 #2 - Incredible!';
    else if (rank === 3) rankText = '🥉 #3 - Amazing!';
    else if (rank <= 10) rankText = `🏆 Top 10 - Rank #${rank}!`;
    
    this.add.text(centerX, centerY + 20, rankText, {
      fontSize: '16px',
      color: rank <= 3 ? '#FFD700' : '#4CAF50',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    
    // Close button
    const closeBtn = this.add.text(centerX, centerY + 60, 'AWESOME!', {
      fontSize: '16px',
      color: '#ffffff',
      backgroundColor: '#4CAF50',
      padding: { x: 15, y: 8 },
      fontFamily: 'Arial',
    })
    .setOrigin(0.5)
    .setInteractive({ useHandCursor: true });
    
    closeBtn.on('pointerdown', () => {
      this.audioManager.playButtonClick();
      overlay.destroy();
      panel.destroy();
      closeBtn.destroy();
      this.children.list.filter(child => 
        child instanceof Phaser.GameObjects.Text && 
        child.text.includes('NEW RECORD')
      ).forEach(child => child.destroy());
    });
    
    // Auto-close after 5 seconds
    this.time.delayedCall(5000, () => {
      if (overlay.active) {
        closeBtn.emit('pointerdown');
      }
    });
  }
} 